import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Gamepad2, Server, CheckCircle, XCircle, Inbox } from "lucide-react";
import { toast } from "sonner";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

export default function CustomPlatformRequestsDialog({ open, onOpenChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  // "Add to MDB" sub-dialog state
  const [addTarget, setAddTarget] = useState(null);
  const [addForm, setAddForm] = useState({ name: "", color: "#A1A1AA", scoreAbbr: "", platformId: "" });
  const [officialPlatforms, setOfficialPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/platform-system-requests?status=pending`);
      setRequests(data.requests || []);
    } catch (err) {
      console.error("Failed to fetch platform/system requests:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchRequests();
      axios.get(`${API}/platforms`).then((r) => setOfficialPlatforms(r.data || [])).catch(() => {});
    }
  }, [open, fetchRequests]);

  const handleAction = async (id, status) => {
    try {
      await axios.put(`${API}/platform-system-requests/${id}/status`, { status });
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Failed to update request:", err);
    }
  };

  // Open the create sub-dialog, pre-filling from the request.
  const openAddDialog = (req) => {
    let platformId = "";
    if (req.type === "system" && req.platformName) {
      const match = officialPlatforms.find(
        (p) => p.name?.toLowerCase() === req.platformName.toLowerCase() || p.id === req.platformName
      );
      platformId = match?.id || "";
    }
    setAddForm({ name: req.name || "", color: "#A1A1AA", scoreAbbr: "", platformId });
    setAddTarget(req);
  };

  const handleCreateFromRequest = async () => {
    if (!addForm.name.trim()) return toast.error("Name is required");
    if (addTarget.type === "system" && !addForm.platformId) return toast.error("Select a parent platform");
    setSaving(true);
    try {
      if (addTarget.type === "platform") {
        await axios.post(`${API}/platforms`, {
          name: addForm.name.trim(),
          color: addForm.color || "#A1A1AA",
          scoreAbbr: (addForm.scoreAbbr || "").slice(0, 3).toUpperCase(),
          isEnabled: true,
        });
      } else {
        await axios.post(`${API}/systems`, {
          platformId: addForm.platformId,
          name: addForm.name.trim(),
        });
      }
      // Auto-mark the request as added.
      try {
        await axios.put(`${API}/platform-system-requests/${addTarget.id}/status`, { status: "added" });
      } catch (e) { /* non-fatal */ }
      toast.success(`${addTarget.type === "platform" ? "Platform" : "System"} "${addForm.name.trim()}" added`);
      setRequests((prev) => prev.filter((r) => r.id !== addTarget.id));
      setAddTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] bg-[#0A0A0A] border-white/10 text-white overflow-hidden flex flex-col" data-testid="ps-requests-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <Server className="w-5 h-5 text-amber-400" />
            Custom Platform & System Requests
            {requests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-mono">
                {requests.length} pending
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Platforms and systems users added to their own libraries and asked to have included in the Master Database.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 -mr-1 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-white/30" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/30">
              <Inbox className="w-10 h-10 mb-2" />
              <p className="text-sm">No pending requests</p>
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map((req) => (
                <div
                  key={req.id}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-white/[0.03] border border-white/5"
                  data-testid={`ps-request-row-${req.id}`}
                >
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${req.type === "platform" ? "bg-purple-500/15 text-purple-400" : "bg-blue-500/15 text-blue-400"}`}>
                    {req.type === "platform" ? <Gamepad2 className="w-4 h-4" /> : <Server className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {req.name}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-white/30">{req.type}</span>
                    </p>
                    <p className="text-white/30 text-[11px] truncate">
                      {req.type === "system" && req.platformName ? `Platform: ${req.platformName} · ` : ""}
                      by {req.requestedByName}
                      {req.voteCount > 1 ? ` · ${req.voteCount} votes` : ""}
                      {req.notes ? ` · "${req.notes}"` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => openAddDialog(req)}
                    className="px-2 py-1 rounded text-[10px] font-bold tracking-wider bg-blue-500/15 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 transition-colors shrink-0"
                    title={`Add this ${req.type} to the Master Database`}
                    data-testid={`ps-request-add-${req.id}`}
                  >
                    ADD
                  </button>
                  <button
                    onClick={() => handleAction(req.id, "added")}
                    className="p-1.5 rounded hover:bg-green-500/20 text-green-400 transition-colors"
                    title="Mark as added"
                    data-testid={`ps-request-approve-${req.id}`}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleAction(req.id, "dismissed")}
                    className="p-1.5 rounded hover:bg-red-500/20 text-red-400/60 transition-colors"
                    title="Dismiss"
                    data-testid={`ps-request-dismiss-${req.id}`}
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      {/* Add to MDB sub-dialog */}
      <Dialog open={!!addTarget} onOpenChange={(v) => { if (!v) setAddTarget(null); }}>
        <DialogContent className="max-w-md bg-[#0A0A0A] border-white/10 text-white" data-testid="ps-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {addTarget?.type === "platform" ? <Gamepad2 className="w-5 h-5 text-purple-400" /> : <Server className="w-5 h-5 text-blue-400" />}
              Add {addTarget?.type === "platform" ? "Platform" : "System"} to MDB
            </DialogTitle>
            <DialogDescription className="text-white/40 text-xs">
              Creating an official {addTarget?.type} from {addTarget?.requestedByName}'s request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">Name</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                className="bg-black/50 border-white/10"
                data-testid="ps-add-name-input"
              />
            </div>
            {addTarget?.type === "system" && (
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">Parent Platform</Label>
                <Select value={addForm.platformId} onValueChange={(v) => setAddForm({ ...addForm, platformId: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="ps-add-platform-select">
                    <SelectValue placeholder="Select a platform" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                    {officialPlatforms.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {addTarget?.type === "platform" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-white/60">Color</Label>
                  <Input
                    type="color"
                    value={addForm.color}
                    onChange={(e) => setAddForm({ ...addForm, color: e.target.value })}
                    className="bg-black/50 border-white/10 h-9 p-1"
                    data-testid="ps-add-color-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-white/60">Score Abbr. (opt.)</Label>
                  <Input
                    value={addForm.scoreAbbr}
                    onChange={(e) => setAddForm({ ...addForm, scoreAbbr: e.target.value.slice(0, 3).toUpperCase() })}
                    placeholder="e.g., GS, XP"
                    maxLength={3}
                    className="bg-black/50 border-white/10"
                    data-testid="ps-add-scoreabbr-input"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddTarget(null)} data-testid="ps-add-cancel-btn">Cancel</Button>
            <Button onClick={handleCreateFromRequest} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="ps-add-create-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create {addTarget?.type === "platform" ? "Platform" : "System"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
